import { fetchRetry } from "./fetchRetry"
import { APP_URL } from "./services"

interface FetchApiInput {
  endpoint: string
  method: "GET" | "POST"
  data?: object
  options?: {
    revalidate?: false | 0 | number
    skipError?: boolean
    cache?: "force-cache" | "no-store"
    skipJson?: boolean
    throwError?: boolean
    tags?: string[]
  }
  retries?: number
  signal?: AbortSignal
}

/**
 * Fetches a data request to an API endpoint
 *
 * @param endpoint - The endpoint you are going to call
 * @param data - Any data you want to pass, like graphQL variables or other parameters
 * @returns The data outputted by that endpoint
 */
export const fetchApi = async <T>({
  data,
  endpoint,
  method = "POST",
  options,
  retries = 1,
  signal,
}: FetchApiInput): Promise<T | null> => {
  try {
    const initBody = {
      body: data ? JSON.stringify(data) : undefined,
      headers: {
        "content-type": "application/json",
      },
      method,
      next: {
        cache: options?.cache,
        revalidate: options?.revalidate,
        tags: options?.tags,
      },
      signal,
    }
    const endpointWithPrefix = `${APP_URL}${endpoint}`

    const res = await fetchRetry(
      endpointWithPrefix,
      initBody,
      retries,
      undefined,
      !!options?.throwError
    )

    if (!res) {
      throw new Error(`No response from ${endpoint} after ${retries} retries`)
    }

    if (options?.skipJson) {
      if (!res.ok) {
        const error = await res.json()
        console.error(error.error)
        throw error
      }
      return res.text() as T
    }

    const json = await res.json()

    if (json.error && !options?.skipError) {
      const error = !options?.throwError
        ? new Error(JSON.stringify(json.error))
        : Object.assign(
            new Error(
              json.error.message || json.error.userMessage || "Request failed"
            ),
            json.error
          )
      console.error(error)
      throw error
    }

    return json
  } catch (error) {
    console.error(`[fetchApi] ${endpoint}:`, error)
    if (options?.throwError) {
      throw error
    }
    return null
  }
}
